import React from 'react';
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ChevronUp, Send, Trash2, PencilIcon, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import whatsappService from "@/services/whatsappService";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState } from "react";

interface Campaign {
  id: string;
  owners: { id: string; name: string }[];
  message: string;
  scheduledDate: Date;
  scheduledTime: string;
  status: 'pending' | 'sent' | 'failed';
}

interface CampaignsListProps {
  campaigns: Campaign[];
  onUpdateStatus: (id: string, status: Campaign['status']) => void;
  onDelete?: (id: string) => void;
  onEdit?: (campaign: Campaign) => void;
}

export function CampaignsList({ campaigns, onUpdateStatus, onDelete, onEdit }: CampaignsListProps) {
  const { toast } = useToast();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState<string[]>([]);

  const handleSendConfirmation = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setConfirmationOpen(true);
  };

  const handleDeleteConfirmation = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedCampaign && onDelete) {
      onDelete(selectedCampaign.id);
      toast({
        title: "Campanha excluída",
        description: "A campanha foi excluída com sucesso.",
      });
    }
    setDeleteConfirmationOpen(false);
    setSelectedCampaign(null);
  };

  const handleConfirmSend = async () => {
    if (!selectedCampaign) return;

    try {
      for (const owner of selectedCampaign.owners) {
        await whatsappService.sendMessage(owner.id, selectedCampaign.message);
      }

      onUpdateStatus(selectedCampaign.id, 'sent');
      toast({
        title: "Mensagem enviada",
        description: `Campanha enviada com sucesso para ${selectedCampaign.owners.length} proprietário(s).`,
      });
    } catch (error) {
      console.error("Erro ao enviar campanha:", error);
      onUpdateStatus(selectedCampaign.id, 'failed');
      toast({
        title: "Erro ao enviar",
        description: "Ocorreu um erro ao enviar a campanha. Tente novamente.",
        variant: "destructive",
      });
    }

    setConfirmationOpen(false);
    setSelectedCampaign(null);
  };

  const toggleCampaignExpansion = (campaignId: string) => {
    setExpandedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Campanhas Cadastradas</h3>
      
      <ScrollArea className="h-[300px] rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Horário</TableHead>
              <TableHead>Proprietários</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.map((campaign) => (
              <React.Fragment key={campaign.id}>
                <TableRow>
                  <TableCell>
                    {format(campaign.scheduledDate, "dd/MM/yyyy", { locale: ptBR })}
                  </TableCell>
                  <TableCell>{campaign.scheduledTime}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCampaignExpansion(campaign.id)}
                      className="flex items-center gap-2"
                    >
                      {campaign.owners.length} selecionado(s)
                      {expandedCampaigns.includes(campaign.id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {campaign.status === 'sent' && <Check className="h-4 w-4 text-green-500" />}
                      {campaign.status === 'failed' && <X className="h-4 w-4 text-red-500" />}
                      {campaign.status === 'pending' && <span className="text-yellow-500">Pendente</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit?.(campaign)}
                        disabled={campaign.status === 'sent'}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteConfirmation(campaign)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendConfirmation(campaign)}
                        disabled={campaign.status === 'sent'}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Enviar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedCampaigns.includes(campaign.id) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div className="p-2 bg-gray-50 rounded-md">
                        <h4 className="text-sm font-medium mb-2">Lista de Proprietários:</h4>
                        <ul className="space-y-1">
                          {campaign.owners.map((owner) => (
                            <li key={owner.id} className="text-sm text-gray-600">
                              {owner.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>

      <AlertDialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar envio</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja enviar esta campanha agora? 
              A mensagem será enviada para {selectedCampaign?.owners.length} proprietário(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSend}>
              Confirmar Envio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmationOpen} onOpenChange={setDeleteConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta campanha? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
              Confirmar Exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
